// src/modules/admin/pages/AdminActivities/index.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Form, Button, Badge, Card, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../shared/hooks/useAuth';
import { useService } from '../../../../shared/hooks/useService';
import { useWarmupTimer } from '../../../../shared/hooks/useWarmupTimer';
import Loading from '../../../../shared/components/Loading';
import ErrorState from '../../../../shared/components/ErrorState';
import { formatDate } from '../../../../shared/utils/dateUtils';
import { formatDuration } from '../../../../shared/utils/formatters';
import ActivityEditModal from '../../components/ActivityEditModal';
import PointsPopover from '../../../../shared/components/PointsPopover';
import './styles.css';

const PAGE_SIZE = 20;

const ACTIVITY_TYPES = [
    { value: '', label: 'All Types' },
    { value: 'running', label: 'Running' },
    { value: 'cycling', label: 'Cycling' },
    { value: 'swimming', label: 'Swimming' },
    { value: 'hiking', label: 'Hiking' },
    { value: 'walking', label: 'Walking' },
    { value: 'football', label: 'Football' },
    { value: 'badminton', label: 'Badminton' },
    { value: 'other', label: 'Other' },
];

const EXEMPTION_STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
];

const ACTIVITY_ICON_MAP = {
    running: 'bi-person-walking',
    cycling: 'bi-bicycle',
    swimming: 'bi-water',
    hiking: 'bi-tree',
    walking: 'bi-person-walking',
    football: 'bi-trophy',
    badminton: 'bi-circle',
    other: 'bi-activity',
};

const ACTIVITY_COLOR_CLASS = {
    running: 'text-danger',
    cycling: 'text-primary',
    swimming: 'text-info',
    hiking: 'text-success',
    walking: 'text-secondary',
    football: 'text-warning',
    badminton: 'text-primary',
    other: 'text-muted',
};

const AdminActivities = () => {
    const service = useService();
    const { hasPermission } = useAuth();
    const hasActivitiesPermission = hasPermission('activities:writeAny');
    const [activities, setActivities] = useState([]);
    const [filteredActivities, setFilteredActivities] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        activityType: '',
        exemptionStatus: '',
        startDate: '',
        endDate: '',
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);
    const [editingActivity, setEditingActivity] = useState(null);
    const { isWarmup } = useWarmupTimer(isLoading);

    // Infinite scroll refs
    const loadMoreRef = useRef(null);
    const nextTokenRef = useRef(null);
    const isLoadingMoreRef = useRef(false);
    const abortRef = useRef(0);
    const observerRef = useRef(null);

    const fetchPage = useCallback(async (append, token, abortId) => {
        try {
            const exemptionStatusParam = filters.exemptionStatus || null;
            const result = await service.getActivities(token, PAGE_SIZE, null, exemptionStatusParam);

            if (abortRef.current !== abortId) return;

            const newActivities = result.activities || [];
            if (append) {
                setActivities(prev => [...prev, ...newActivities]);
            } else {
                setActivities(newActivities);
            }

            nextTokenRef.current = result.nextPageToken || null;
            setHasMore(!!result.nextPageToken);
        } catch (err) {
            if (abortRef.current !== abortId) return;
            setError(err.message || 'Failed to fetch activities');
        } finally {
            if (abortRef.current !== abortId) return;
            setIsLoading(false);
            isLoadingMoreRef.current = false;
            setIsLoadingMore(false);
        }
    }, [service, filters.exemptionStatus]);

    // Reset and load first page when exemption filter changes
    useEffect(() => {
        const abortId = ++abortRef.current;
        nextTokenRef.current = null;
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
        setHasMore(false);
        setIsLoading(true);
        setError(null);
        fetchPage(false, null, abortId);
    }, [filters.exemptionStatus, fetchPage]);

    // Client-side search filtering
    useEffect(() => {
        let result = [...activities];
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            result = result.filter(a => {
                const username = (a.username || '').toLowerCase();
                const fullName = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
                return username.includes(term) || fullName.includes(term);
            });
        }
        setFilteredActivities(result);
    }, [searchTerm, activities]);

    // IntersectionObserver for infinite scroll
    useEffect(() => {
        if (isLoading) return;

        const observer = new IntersectionObserver(
            entries => {
                if (
                    entries[0].isIntersecting &&
                    nextTokenRef.current &&
                    !isLoadingMoreRef.current
                ) {
                    const abortId = abortRef.current;
                    isLoadingMoreRef.current = true;
                    setIsLoadingMore(true);
                    fetchPage(true, nextTokenRef.current, abortId);
                }
            },
            { rootMargin: '200px' }
        );

        observerRef.current = observer;

        const currentRef = loadMoreRef.current;
        if (currentRef) observer.observe(currentRef);

        return () => observer.disconnect();
    }, [isLoading, hasMore, fetchPage]);

    // Re-observe sentinel after activities update so observer re-fires if still visible
    useEffect(() => {
        if (isLoading) return;
        requestAnimationFrame(() => {
            if (observerRef.current && loadMoreRef.current) {
                observerRef.current.disconnect();
                observerRef.current.observe(loadMoreRef.current);
            }
        });
    }, [activities, isLoading]);

    const applyFilters = () => {
        const abortId = ++abortRef.current;
        nextTokenRef.current = null;
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
        setHasMore(false);
        setIsLoading(true);
        setError(null);
        fetchPage(false, null, abortId);
    };

    const clearFilters = () => {
        setFilters({
            activityType: '',
            exemptionStatus: '',
            startDate: '',
            endDate: '',
        });
        setSearchTerm('');
    };

    const handleDelete = async (activityId) => {
        if (!window.confirm('Delete this activity? This cannot be undone.')) return;
        try {
            await service.deleteActivityAsAdmin(activityId);
            setActivities(prev => prev.filter(a => a.activityId !== activityId));
        } catch (err) {
            setError(err.message || 'Failed to delete activity');
        }
    };

    const handleEditSave = (activityId, updatedPayload) => {
        setActivities(prev => prev.map(a => {
            if (a.activityId !== activityId) return a;
            return {
                ...a,
                activityType: updatedPayload.type,
                date: updatedPayload.date,
                distance: updatedPayload.distance,
                duration: updatedPayload.duration,
                elevation: updatedPayload.elevation,
                calories: updatedPayload.calories,
            };
        }));
    };

    const getInitials = (firstName, lastName, username) => {
        if (firstName && lastName) {
            return `${firstName[0]}${lastName[0]}`.toUpperCase();
        }
        if (firstName) {
            return firstName[0].toUpperCase();
        }
        return (username || '?')[0].toUpperCase();
    };

    const getExemptionBadgeVariant = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'approved': return 'success';
            case 'rejected': return 'danger';
            default: return null;
        }
    };

    if (isLoading) return <Loading isWarmup={isWarmup} />;
    if (!hasActivitiesPermission) {
        return (
            <Container className="admin-activities-container">
                <Alert variant="warning" className="admin-no-permission-alert">
                    <Alert.Heading>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        No Permission
                    </Alert.Heading>
                    <p>You need the <strong>activities:writeAny</strong> permission to view and manage activities.</p>
                    <Button variant="outline-warning" size="sm" as={Link} to="/admin">
                        <i className="bi bi-arrow-left me-1"></i>
                        Back to Dashboard
                    </Button>
                </Alert>
            </Container>
        );
    }
    if (error) return <ErrorState message={error} onRetry={() => applyFilters()} />;

    return (
        <Container className="admin-activities-container">
            {/* Search bar */}
            <div className="admin-activities-search">
                <Form.Control
                    type="text"
                    placeholder="Search by username or name..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Filter controls */}
            <div className="admin-activities-filters">
                <Form.Select
                    value={filters.activityType}
                    onChange={e => setFilters(prev => ({ ...prev, activityType: e.target.value }))}
                    className="flex-fill"
                >
                    {ACTIVITY_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                </Form.Select>

                <Form.Select
                    value={filters.exemptionStatus}
                    onChange={e => setFilters(prev => ({ ...prev, exemptionStatus: e.target.value }))}
                    className="flex-fill"
                >
                    {EXEMPTION_STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </Form.Select>

                <Form.Control
                    type="date"
                    value={filters.startDate}
                    onChange={e => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    className="flex-fill"
                />

                <Form.Control
                    type="date"
                    value={filters.endDate}
                    onChange={e => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    className="flex-fill"
                />

                <Button variant="primary" onClick={applyFilters}>
                    Apply Filters
                </Button>
                <Button variant="outline-secondary" onClick={clearFilters}>
                    Clear Filters
                </Button>
            </div>

            {/* Activity cards grid */}
            {filteredActivities.length > 0 ? (
                <div className="admin-activities-grid">
                    {filteredActivities.map(activity => (
                        <Card key={activity.activityId} className="admin-activity-card">
                            <Card.Body>
                                {/* User info */}
                                <div className="admin-activity-user">
                                    {activity.profilePicture ? (
                                        <img
                                            src={activity.profilePicture}
                                            alt={`${activity.firstName || activity.username || 'User'}'s avatar`}
                                            className="admin-activity-avatar"
                                        />
                                    ) : (
                                        <div className="admin-activity-avatar admin-activity-avatar-initials">
                                            {getInitials(activity.firstName, activity.lastName, activity.username)}
                                        </div>
                                    )}
                                    <div className="admin-activity-user-name">
                                        {activity.firstName || activity.lastName
                                            ? `${activity.firstName || ''} ${activity.lastName || ''}`.trim()
                                            : activity.username || 'Unknown User'
                                        }
                                    </div>
                                </div>

                                {/* Activity type */}
                                <div className="admin-activity-type">
                                    <i className={`bi ${ACTIVITY_ICON_MAP[activity.activityType] || 'bi-activity'} me-2 ${ACTIVITY_COLOR_CLASS[activity.activityType] || 'text-muted'}`}></i>
                                    <span className="text-capitalize">{activity.activityType}</span>
                                </div>

                                {/* Stats */}
                                <div className="admin-activity-stats">
                                    <div className="admin-stat">
                                        <span className="admin-stat-label">Distance</span>
                                        <span className="admin-stat-value">
                                            {activity.distance != null ? `${parseFloat(activity.distance).toFixed(2)} km` : '—'}
                                        </span>
                                    </div>
                                    <div className="admin-stat">
                                        <span className="admin-stat-label">Duration</span>
                                        <span className="admin-stat-value">
                                            {activity.duration != null ? formatDuration(activity.duration) : '—'}
                                        </span>
                                    </div>
                                    <div className="admin-stat">
                                        <span className="admin-stat-label">Date</span>
                                        <span className="admin-stat-value">
                                            {activity.createDateTime ? formatDate(activity.createDateTime, 'MMM d, yyyy') : '—'}
                                        </span>
                                    </div>
                                </div>

                                {/* Points with penalty */}
                                <PointsPopover activity={activity} popoverId={`points-popover-admin-${activity.activityId}`} placement="left">
                                    <div className="admin-activity-points" style={{ cursor: 'help' }}>
                                        {activity.points != null ? `${parseFloat(activity.points).toFixed(2)} points` : ''}
                                        {activity.penaltyFactor != null && activity.penaltyFactor < 1.0 && (
                                            <Badge bg="warning" className="ms-2 penalty-badge" title="Penalty applied">
                                                <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                                {activity.penaltyFactor.toFixed(1)}×
                                            </Badge>
                                        )}
                                    </div>
                                </PointsPopover>

                                {/* Exemption status badge */}
                                {activity.exemptionStatus && (
                                    <div className="admin-activity-exemption">
                                        <Badge variant={getExemptionBadgeVariant(activity.exemptionStatus)}>
                                            {activity.exemptionStatus.charAt(0).toUpperCase() + activity.exemptionStatus.slice(1)}
                                        </Badge>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="admin-activity-actions">
                                    <Link to={`/activity/${activity.activityId}`} className="admin-activity-link">
                                        View
                                    </Link>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => setEditingActivity(activity)}
                                    >
                                        <i className="bi bi-pencil me-1"></i>
                                        Edit
                                    </Button>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleDelete(activity.activityId)}
                                    >
                                        Delete
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}
                </div>
            ) : (
                <div className="admin-activities-empty">
                    <div className="admin-activities-empty-icon">🏃</div>
                    <h3>No activities found</h3>
                    <p>
                        {searchTerm
                            ? `No activities match "${searchTerm}".`
                            : 'There are no activities to display.'}
                    </p>
                </div>
            )}

            {/* Load more / infinite scroll sentinel */}
            <div className="admin-activities-load-more" ref={loadMoreRef}>
                {isLoadingMore && (
                    <div className="admin-activities-loading">
                        <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                        ></span>
                        Loading more activities…
                    </div>
                )}
            </div>

            <ActivityEditModal
                show={!!editingActivity}
                activity={editingActivity}
                onSave={(activityId, payload) => {
                    handleEditSave(activityId, payload);
                    setEditingActivity(null);
                }}
                onClose={() => setEditingActivity(null)}
            />
        </Container>
    );
};

export default AdminActivities;
